import { createSlice } from '../factory';
import { getListingsCount } from '@repo/api';
import { runWithDedup } from '../utils/dedup';
import { calculateExpectedMoveInDates, getUpcomingMonths, } from '@repo/utils';
let listingsCountInFlightKey = null;
let listingsCountInFlight = null;
/**
 * IMPORTANT: Our internal UI state uses `buildingStatus.isRentStabilized`,
 * but the BE/API filter key is `isBuildingRentStabilized`.
 * This helper maps UI state -> API payload shape.
 */
const buildApiFiltersFromState = (state) => {
    const { price, bedrooms, minNumBathrooms, neighborhoodIds, buildingStatus, expectedMoveInDate, compliances, amenities, } = state;
    return {
        minRent: price?.min ?? null,
        maxRent: price?.max ?? null,
        numBedrooms: Array.isArray(bedrooms) ? bedrooms : [],
        minNumBathrooms: typeof minNumBathrooms === 'number' ? minNumBathrooms : null,
        ...(Array.isArray(neighborhoodIds) && neighborhoodIds.length > 0 ? { neighborhoodIds } : {}),
        // Map UI state key -> BE/API key
        isBuildingRentStabilized: buildingStatus?.isRentStabilized ?? null,
        isVerified: buildingStatus?.isVerified ?? null,
        hasBuildingGoodCauseEviction: buildingStatus?.hasBuildingGoodCauseEviction ?? null,
        isTopRated: buildingStatus?.isTopRated ?? null,
        ...compliances,
        ...amenities,
        minExpectedMoveInDate: expectedMoveInDate?.min ?? null,
        maxExpectedMoveInDate: expectedMoveInDate?.max ?? null,
    };
};
const initialListingsFiltersState = {
    price: { min: null, max: null },
    bedrooms: [],
    minNumBathrooms: null,
    neighborhoodIds: [],
    listingsCount: null,
    isCountLoading: false,
    shouldRefresh: false,
    map: {
        boundaries: null,
        zoom: 10,
    },
    buildingStatus: {
        isRentStabilized: null,
        isVerified: true,
        hasBuildingGoodCauseEviction: null,
        isTopRated: null,
    },
    expectedMoveInDate: {
        min: null,
        max: null,
        selections: [],
    },
    compliances: {
        hasNoBuildingOneYearBedbugHistory: null,
        hasNoBuildingEvictionHistory: null,
        hasNoBuildingPendingLitigation: null,
        hasNoBuildingOpenViolations: null,
    },
    amenities: {
        // Unit amenities
        isUnitPetsAllowed: null,
        isUnitWasherDryer: null,
        isUnitPrivateOutdoor: null,
        isUnitDishwasher: null,
        isUnitAirConditioned: null,
        // Building amenities
        isBuildingOutdoor: null,
        isBuildingGym: null,
        isBuildingPool: null,
        isBuildingParking: null,
        isBuildingDoorman: null,
        isBuildingLaundryRoom: null,
        isBuildingElevator: null,
    },
    building: null,
};
export const listingsFiltersSlice = createSlice({
    name: 'listingsFilters',
    initialState: initialListingsFiltersState,
    persist: {
        keys: [
            'price',
            'bedrooms',
            'minNumBathrooms',
            'neighborhoodIds',
            'expectedMoveInDate',
            'compliances',
            'amenities',
            'buildingStatus',
            'building',
        ],
    },
    actions: ({ setSlice, getSlice, get, }) => ({
        getApiFilterPayload: () => {
            const { price, bedrooms, minNumBathrooms, neighborhoodIds, buildingStatus, expectedMoveInDate, compliances, amenities, map, building, } = getSlice();
            const filters = buildApiFiltersFromState({
                price,
                bedrooms,
                minNumBathrooms,
                neighborhoodIds,
                buildingStatus,
                expectedMoveInDate,
                compliances,
                amenities,
            });
            return {
                filters,
                mapBoundaries: map?.boundaries,
                buildingSlugs: building?.slug ? [building.slug] : [],
            };
        },
        updateListingsCount: async (options) => {
            const { withMapBoundaries = true } = options ?? {};
            const { price, bedrooms, minNumBathrooms, neighborhoodIds, buildingStatus, expectedMoveInDate, compliances, amenities, map, building, } = getSlice();
            const filters = buildApiFiltersFromState({
                price,
                bedrooms,
                minNumBathrooms,
                neighborhoodIds,
                buildingStatus,
                expectedMoveInDate,
                compliances,
                amenities,
            });
            const payload = {
                filters,
                mapBoundaries: withMapBoundaries ? map.boundaries : null,
                buildingSlugs: building?.slug ? [building.slug] : [],
            };
            const key = JSON.stringify(payload);
            if (listingsCountInFlight && listingsCountInFlightKey === key) {
                return listingsCountInFlight;
            }
            setSlice({ isCountLoading: true });
            try {
                listingsCountInFlightKey = key;
                listingsCountInFlight = (async () => {
                    try {
                        const result = await getListingsCount(payload);
                        setSlice({ listingsCount: result.count, isCountLoading: false });
                    }
                    catch {
                        // Don't leave the UI stuck in loading if count fetch fails (e.g. 401/403/network).
                        setSlice({ isCountLoading: false });
                    }
                    finally {
                        listingsCountInFlight = null;
                        listingsCountInFlightKey = null;
                    }
                })();
                await listingsCountInFlight;
            }
            catch {
                // Don't leave the UI stuck in loading if count fetch fails (e.g. 401/403/network).
                setSlice({ isCountLoading: false });
            }
        },
        setListingsCount: (count) => {
            setSlice({ listingsCount: count });
        },
        updateFilters: (next) => {
            setSlice({ ...getSlice(), ...next });
        },
        setBuilding: (next) => {
            setSlice({ building: next });
        },
        resetBuilding: () => {
            setSlice({ building: null });
        },
        setPrice: (next) => {
            setSlice({ price: { ...getSlice().price, ...next } });
        },
        resetPrice: () => setSlice({ price: { min: null, max: null } }),
        toggleBedroom: (value) => {
            setSlice(prev => {
                const current = Array.isArray(prev.bedrooms) ? prev.bedrooms : [];
                const exists = current.includes(value);
                const next = exists ? current.filter(v => v !== value) : [...current, value];
                next.sort((a, b) => a - b);
                return { bedrooms: next };
            });
        },
        resetBedrooms: () => setSlice({ bedrooms: [] }),
        // Singular add/remove were unused; prefer bulk helpers for efficiency
        addNeighborhoods: (neighborhoodIds) => {
            setSlice(prev => {
                const current = new Set(Array.isArray(prev.neighborhoodIds) ? prev.neighborhoodIds : []);
                for (const id of neighborhoodIds)
                    current.add(id);
                return { neighborhoodIds: Array.from(current) };
            });
        },
        removeNeighborhoods: (neighborhoodIds) => {
            setSlice(prev => {
                const removeSet = new Set(neighborhoodIds);
                const current = Array.isArray(prev.neighborhoodIds) ? prev.neighborhoodIds : [];
                const next = current.filter(id => !removeSet.has(id));
                return { neighborhoodIds: next };
            });
        },
        toggleNeighborhood: (neighborhoodId) => {
            setSlice(prev => {
                const current = Array.isArray(prev.neighborhoodIds) ? prev.neighborhoodIds : [];
                const exists = current.includes(neighborhoodId);
                const next = exists
                    ? current.filter(id => id !== neighborhoodId)
                    : [...current, neighborhoodId];
                return { neighborhoodIds: next };
            });
        },
        toggleNeighborhoodWithChildren: (neighborhoodId) => {
            const accordion = get()?.neighborhoods?.neighborhoodAccordionData;
            if (!accordion) {
                // Fallback to single toggle if accordion data not available
                setSlice(prev => {
                    const current = Array.isArray(prev.neighborhoodIds) ? prev.neighborhoodIds : [];
                    const exists = current.includes(neighborhoodId);
                    const next = exists
                        ? current.filter(id => id !== neighborhoodId)
                        : [...current, neighborhoodId];
                    return { neighborhoodIds: next };
                });
                return;
            }
            // Try to find this id as a parent in any borough
            let childrenIdsForParent = null;
            for (const entry of Object.values(accordion)) {
                if (entry.parents.some(p => p.id === neighborhoodId)) {
                    const children = entry.childrenByParent[neighborhoodId] || [];
                    childrenIdsForParent = children.map(c => c.id);
                    break;
                }
            }
            if (!childrenIdsForParent) {
                // Not a parent; toggle single
                setSlice(prev => {
                    const current = Array.isArray(prev.neighborhoodIds) ? prev.neighborhoodIds : [];
                    const exists = current.includes(neighborhoodId);
                    const next = exists
                        ? current.filter(id => id !== neighborhoodId)
                        : [...current, neighborhoodId];
                    return { neighborhoodIds: next };
                });
                return;
            }
            // Parent toggle semantics: if parent is selected, remove parent + all children.
            // Otherwise add parent + all children (ensure full group is selected).
            const childIds = childrenIdsForParent;
            setSlice(prev => {
                const current = Array.isArray(prev.neighborhoodIds) ? prev.neighborhoodIds : [];
                const selected = new Set(current);
                const parentSelected = selected.has(neighborhoodId);
                if (parentSelected) {
                    // Remove parent and all its children
                    selected.delete(neighborhoodId);
                    for (const id of childIds)
                        selected.delete(id);
                }
                else {
                    // Add parent and all its children
                    selected.add(neighborhoodId);
                    for (const id of childIds)
                        selected.add(id);
                }
                return { neighborhoodIds: Array.from(selected) };
            });
        },
        toggleBorough: (borough) => {
            const entry = get()?.neighborhoods?.neighborhoodAccordionData?.[borough];
            if (!entry)
                return;
            const allIds = [...entry.parents.map(p => p.id)];
            const childGroups = Object.values(entry.childrenByParent);
            for (const group of childGroups) {
                for (const c of group)
                    allIds.push(c.id);
            }
            setSlice(prev => {
                const current = Array.isArray(prev.neighborhoodIds) ? prev.neighborhoodIds : [];
                const selected = new Set(current);
                const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));
                if (allSelected) {
                    allIds.forEach(id => selected.delete(id));
                }
                else {
                    allIds.forEach(id => selected.add(id));
                }
                return { neighborhoodIds: Array.from(selected) };
            });
        },
        resetNeighborhoods: () => setSlice({ neighborhoodIds: [] }),
        // Building status filter actions
        toggleBuildingStatusFilter: (filterKey) => {
            setSlice(prev => {
                const current = prev.buildingStatus[filterKey];
                return {
                    buildingStatus: {
                        ...prev.buildingStatus,
                        [filterKey]: current === true ? null : true,
                    },
                };
            });
        },
        setBuildingStatusFilter: (filterKey, value) => {
            setSlice(prev => ({
                buildingStatus: {
                    ...prev.buildingStatus,
                    [filterKey]: value,
                },
            }));
        },
        resetBuildingStatusFilter: (filterKey) => {
            setSlice(prev => ({
                buildingStatus: {
                    ...prev.buildingStatus,
                    [filterKey]: null,
                },
            }));
        },
        resetAllBuildingStatusFilters: () => {
            setSlice({
                buildingStatus: {
                    isRentStabilized: null,
                    isVerified: null,
                    hasBuildingGoodCauseEviction: null,
                    isTopRated: null,
                },
            });
        },
        // Expected move-in date filter actions
        setExpectedMoveInDate: (next) => {
            setSlice(prev => ({
                expectedMoveInDate: {
                    ...prev.expectedMoveInDate,
                    ...next,
                },
            }));
        },
        resetExpectedMoveInDate: () => {
            setSlice({ expectedMoveInDate: { min: null, max: null, selections: [] } });
        },
        toggleExpectedMoveInSelection: (selection) => {
            setSlice(prev => {
                const current = (Array.isArray(prev.expectedMoveInDate.selections)
                    ? prev.expectedMoveInDate.selections
                    : []);
                // Move-in is a RANGE (min + max). Chip rules — mirrors the
                // Renter Profile wizard so behavior is consistent everywhere
                // we surface these chips:
                //   • nothing selected              → select just this chip
                //   • tapped inside the range       → collapse to just this
                //     (or deselect entirely if it was the only chip)
                //   • tapped outside the range      → extend to include it,
                //                                     filling any gap so the
                //                                     range stays contiguous
                // Canonical order = ['asap', ...upcomingMonths]. `filter(i>=0)`
                // guards against a stray selection value not in the current
                // month window (edge case around month rollovers).
                const order = [
                    'asap',
                    ...getUpcomingMonths().map(m => m.key),
                ];
                const clickedIdx = order.indexOf(selection);
                const selectedIdxs = current
                    .map(k => order.indexOf(k))
                    .filter(i => i >= 0)
                    .sort((a, b) => a - b);
                let nextSelections;
                if (clickedIdx < 0) {
                    // Unknown selection value — fall back to legacy toggle so
                    // we never freeze a bad state.
                    nextSelections = current.includes(selection)
                        ? current.filter(s => s !== selection)
                        : [...current, selection];
                }
                else if (selectedIdxs.length === 0) {
                    nextSelections = [selection];
                }
                else {
                    const minIdx = selectedIdxs[0];
                    const maxIdx = selectedIdxs[selectedIdxs.length - 1];
                    const inRange = clickedIdx >= minIdx && clickedIdx <= maxIdx;
                    if (inRange) {
                        nextSelections = minIdx === maxIdx ? [] : [selection];
                    }
                    else {
                        const newMin = Math.min(minIdx, clickedIdx);
                        const newMax = Math.max(maxIdx, clickedIdx);
                        nextSelections = order.slice(newMin, newMax + 1);
                    }
                }
                const dates = calculateExpectedMoveInDates(nextSelections);
                return {
                    expectedMoveInDate: {
                        ...prev.expectedMoveInDate,
                        selections: nextSelections,
                        min: dates?.min ?? null,
                        max: dates?.max ?? null,
                    },
                };
            });
        },
        setExpectedMoveInSelections: (selections) => {
            const dates = calculateExpectedMoveInDates(selections);
            setSlice({
                expectedMoveInDate: {
                    selections,
                    min: dates?.min ?? null,
                    max: dates?.max ?? null,
                },
            });
        },
        getUrlFilterPayload: () => {
            const { price, bedrooms, minNumBathrooms, neighborhoodIds, buildingStatus, expectedMoveInDate, compliances, amenities, building, } = getSlice();
            const segments = [];
            // Helper to add segment if values exist
            const addSegment = (key, values) => {
                if (values.length > 0) {
                    segments.push(`${key}:${values.join(',')}`);
                }
            };
            /**
             * Filter-URL slug: lowercase, spaces → hyphens.
             *
             * Every place name in the path must go through this. Boroughs used
             * to be lowercased only, which emitted `borough:staten island` —
             * a URL with a literal space that resolves but is *not* the
             * canonical form (`borough:staten-island`), so users copied and
             * shared indexable duplicates. Only "Staten Island" and "The
             * Bronx" were affected, which is why it survived so long.
             */
            const toUrlSlug = (value) => value.toLowerCase().replace(/\s+/g, '-');
            // Neighborhoods and Boroughs - Process first to determine borough/nbr segments
            let boroughNames = new Set();
            let neighborhoodNames = new Set();
            if (Array.isArray(neighborhoodIds) && neighborhoodIds.length > 0) {
                const neighborhoods = (get()?.neighborhoods?.neighborhoods || []);
                const byId = new Map();
                for (const n of neighborhoods)
                    byId.set(n.id, n);
                const accordion = get()?.neighborhoods?.neighborhoodAccordionData;
                const selectedSet = new Set(neighborhoodIds);
                if (accordion) {
                    // Check for fully selected boroughs
                    for (const [borough, entry] of Object.entries(accordion)) {
                        const allIds = [];
                        for (const p of entry.parents)
                            allIds.push(p.id);
                        for (const group of Object.values(entry.childrenByParent)) {
                            for (const c of group)
                                allIds.push(c.id);
                        }
                        if (allIds.length > 0 && allIds.every(id => selectedSet.has(id))) {
                            boroughNames.add(toUrlSlug(borough));
                            allIds.forEach(id => selectedSet.delete(id));
                        }
                    }
                }
                // Process remaining neighborhood selections
                for (const id of Array.from(selectedSet)) {
                    const n = byId.get(id);
                    if (n) {
                        // Examples: "SoHo" -> "soho", "All Upper Manhattan" -> "all-upper-manhattan"
                        neighborhoodNames.add(toUrlSlug(n.name));
                        boroughNames.add(toUrlSlug(n.borough));
                    }
                }
            }
            // 1. Borough (always first if present)
            addSegment('borough', Array.from(boroughNames));
            // 2. Neighborhoods (only if there are individual neighborhoods)
            addSegment('nbr', Array.from(neighborhoodNames));
            // 3. Building slugs
            if (building?.slug) {
                segments.push(`building:${building.addressSlug}`);
                segments.push(`building_slugs:${building.slug}`);
                // Note: building_slugs format would be used if we support multiple buildings in the future
            }
            // 4. Price range
            if (price?.min !== null || price?.max !== null) {
                const min = price.min ?? '';
                const max = price.max ?? '';
                // Only add if at least one value is not empty
                if (min !== '' || max !== '') {
                    segments.push(`price:${min}-${max}`);
                }
            }
            // 5. Bedrooms
            if (Array.isArray(bedrooms) && bedrooms.length > 0) {
                addSegment('bedrooms', bedrooms.map(String));
            }
            // 6. Bathrooms
            if (minNumBathrooms !== null) {
                // For URL, include all bathroom values from min up to 4+
                const bathroomValues = [1, 2, 3, 4].filter(v => v >= minNumBathrooms);
                addSegment('bathrooms', bathroomValues.map(String));
            }
            // 7. Move-in date range
            if (expectedMoveInDate?.min || expectedMoveInDate?.max) {
                const min = expectedMoveInDate.min ?? '';
                const max = expectedMoveInDate.max ?? '';
                // Only add if at least one value is not empty
                if (min !== '' || max !== '') {
                    segments.push(`movein:${min}-${max}`);
                }
            }
            // 8. Amenities - map keys to slugs
            const amenitySlugs = [];
            const amenityMapping = {
                isUnitWasherDryer: 'washer-dryer',
                isUnitDishwasher: 'dishwasher',
                isUnitAirConditioned: 'air-conditioned',
                isUnitPrivateOutdoor: 'private-outdoor',
                isBuildingLaundryRoom: 'laundry',
                isBuildingElevator: 'elevator',
                isBuildingDoorman: 'doorman',
                isBuildingGym: 'gym',
                isBuildingParking: 'parking',
                isBuildingOutdoor: 'outdoor-space',
                isBuildingPool: 'pool',
                isUnitPetsAllowed: 'pets',
            };
            for (const [key, slug] of Object.entries(amenityMapping)) {
                if (amenities[key] === true) {
                    amenitySlugs.push(slug);
                }
            }
            addSegment('amenities', amenitySlugs);
            // 9. Compliance - map keys to slugs
            const complianceSlugs = [];
            const complianceMapping = {
                hasNoBuildingOneYearBedbugHistory: 'bedbug',
                hasNoBuildingEvictionHistory: 'eviction',
                hasNoBuildingPendingLitigation: 'litigation',
                hasNoBuildingOpenViolations: 'violations',
            };
            for (const [key, slug] of Object.entries(complianceMapping)) {
                if (compliances[key] === true) {
                    complianceSlugs.push(slug);
                }
            }
            addSegment('compliance', complianceSlugs);
            // 10. Building status - map keys to slugs
            const statusSlugs = [];
            const statusMapping = {
                isRentStabilized: 'rent-stabilized',
                isVerified: 'verified',
                hasBuildingGoodCauseEviction: 'good-cause',
                isTopRated: 'top-rated',
            };
            for (const [key, slug] of Object.entries(statusMapping)) {
                if (buildingStatus[key] === true) {
                    statusSlugs.push(slug);
                }
            }
            addSegment('status', statusSlugs);
            return segments.join('|');
        },
        getLocationSelectionLabel: () => {
            if (getSlice().building?.address) {
                return getSlice().building?.address;
            }
            const selected = Array.isArray(getSlice().neighborhoodIds) ? getSlice().neighborhoodIds : [];
            if (selected.length === 0)
                return undefined;
            const neighborhoods = (get()?.neighborhoods?.neighborhoods || []);
            const byId = new Map();
            for (const n of neighborhoods)
                byId.set(n.id, n);
            const accordion = get()?.neighborhoods?.neighborhoodAccordionData;
            const selectedSet = new Set(selected);
            const boroughAllIds = {};
            const fullySelectedBoroughs = [];
            const childToRootParent = {};
            if (accordion) {
                for (const [borough, entry] of Object.entries(accordion)) {
                    const ids = [];
                    for (const p of entry.parents)
                        ids.push(p.id);
                    for (const [parentId, group] of Object.entries(entry.childrenByParent)) {
                        for (const c of group) {
                            ids.push(c.id);
                            childToRootParent[c.id] = parentId; // map every descendant to its top-level parent
                        }
                    }
                    boroughAllIds[borough] = ids;
                    if (ids.length > 0 && ids.every(id => selectedSet.has(id)))
                        fullySelectedBoroughs.push(borough);
                }
            }
            // Helper: map an id to its canonical label considering aggregation
            const idToCanonicalLabel = (id) => {
                // If this id belongs to a fully-selected borough -> All Borough
                if (accordion) {
                    for (const [borough, ids] of Object.entries(boroughAllIds)) {
                        if (fullySelectedBoroughs.includes(borough) && ids.includes(id)) {
                            return `All ${borough}`;
                        }
                    }
                }
                const n = byId.get(id);
                if (!n)
                    return undefined;
                // If any ancestor (top-level parent) is selected, collapse to that parent's label
                const rootParentId = childToRootParent[id];
                if (rootParentId && selectedSet.has(rootParentId)) {
                    const root = byId.get(rootParentId);
                    return root?.name || undefined;
                }
                // If parent is selected, use parent name
                if (n.parentId && selectedSet.has(n.parentId)) {
                    const p = byId.get(n.parentId);
                    return p?.name || undefined;
                }
                return n.name;
            };
            // Build final tokens set (distinct) for count
            const tokens = new Set();
            // 1) add fully selected boroughs
            for (const b of fullySelectedBoroughs)
                tokens.add(`All ${b}`);
            // 2) add selected parents (skip parents in fully-selected boroughs)
            for (const id of selected) {
                const n = byId.get(id);
                if (n && (n.parentId === null || n.parentId === undefined)) {
                    const inFullySelectedBorough = fullySelectedBoroughs.includes(n.borough);
                    if (!inFullySelectedBorough)
                        tokens.add(n.name);
                }
            }
            // 3) add remaining singles (children/standalone not covered by parent or borough)
            for (const id of selected) {
                const label = idToCanonicalLabel(id);
                if (label)
                    tokens.add(label);
            }
            const labels = Array.from(tokens);
            if (labels.length === 0)
                return undefined;
            // Determine first label based on actual selection order
            let firstLabel;
            for (const id of selected) {
                const lbl = idToCanonicalLabel(id);
                if (lbl) {
                    firstLabel = lbl;
                    break;
                }
            }
            if (!firstLabel)
                firstLabel = labels[0];
            if (labels.length === 1)
                return firstLabel;
            const extra = labels.length - 1;
            return `${firstLabel} + ${extra} ${extra === 1 ? 'location' : 'locations'}`;
        },
        // Bathroom filter actions
        setMinNumBathrooms: (value) => {
            setSlice({ minNumBathrooms: typeof value === 'number' ? value : null });
        },
        resetMinNumBathrooms: () => setSlice({ minNumBathrooms: null }),
        // Compliance filter actions
        toggleComplianceFilter: (filterKey) => {
            setSlice(prev => {
                const current = prev.compliances[filterKey];
                return {
                    compliances: {
                        ...prev.compliances,
                        [filterKey]: current === null ? true : current === true ? false : null,
                    },
                };
            });
        },
        setComplianceFilter: (filterKey, value) => {
            setSlice(prev => ({
                compliances: {
                    ...prev.compliances,
                    [filterKey]: value,
                },
            }));
        },
        resetComplianceFilter: (filterKey) => {
            setSlice(prev => ({
                compliances: {
                    ...prev.compliances,
                    [filterKey]: null,
                },
            }));
        },
        resetAllCompliances: () => {
            setSlice({
                compliances: {
                    hasNoBuildingOneYearBedbugHistory: null,
                    hasNoBuildingEvictionHistory: null,
                    hasNoBuildingPendingLitigation: null,
                    hasNoBuildingOpenViolations: null,
                },
            });
        },
        // Amenity filter actions
        toggleAmenityFilter: (filterKey) => {
            setSlice(prev => {
                const current = prev.amenities[filterKey];
                return {
                    amenities: {
                        ...prev.amenities,
                        [filterKey]: !current,
                    },
                };
            });
        },
        setAmenityFilter: (filterKey, value) => {
            setSlice(prev => ({
                amenities: {
                    ...prev.amenities,
                    [filterKey]: value,
                },
            }));
        },
        resetAmenityFilter: (filterKey) => {
            setSlice(prev => ({
                amenities: {
                    ...prev.amenities,
                    [filterKey]: null,
                },
            }));
        },
        resetAllAmenities: () => {
            setSlice({
                amenities: initialListingsFiltersState.amenities,
            });
        },
        resetAllFilters: () => {
            // Access initial state directly since it's extracted as a module-level constant
            setSlice({
                ...initialListingsFiltersState,
                buildingStatus: { ...initialListingsFiltersState.buildingStatus, isVerified: false },
            });
        },
        triggerRefresh: () => {
            setSlice({ shouldRefresh: true });
        },
        clearRefreshFlag: () => {
            setSlice({ shouldRefresh: false });
        },
        setMapBoundaries: (boundaries) => {
            setSlice({ map: { ...getSlice().map, boundaries } });
        },
        setMapZoom: (zoom) => {
            setSlice({ map: { ...getSlice().map, zoom } });
        },
    }),
});
